import { useRef } from "react";
import { gsap } from "../../animations/gsapSetup.ts";
import { useGsapScrollTrigger } from "../../hooks/useGsapScrollTrigger.ts";
import { useIsMobile } from "../../hooks/useMediaQuery.ts";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";
import { useI18n } from "../../i18n/context.tsx";
import { HrZoneMatrix } from "./HrZoneMatrix.tsx";
import styles from "./VoiceTranscript.module.css";

/* ── Mini bar chart for Zone FG% ── */
const ZONE_BARS = [
  { label: "Paint", pct: 85, color: "var(--color-ai-secondary)" },
  { label: "Mid", pct: 58, color: "var(--color-ai-primary)" },
  { label: "3PT", pct: 34, color: "#ff9500" },
  { label: "Deep", pct: 18, color: "#ff3b30" },
];

/* ── Arc trajectory: flat miss path ── */
const ARC_FLAT = "M 40 85 Q 140 30 240 66";

/* ── 3D Error Meter: surge points for Short/Long/Flat/Left/Right ── */
const ERROR_RANKINGS = {
  direction: [
    {
      rank: 1,
      shot: "Left-wing 3PT @ HR170+",
      value: "Left 72%",
      severity: "critical" as const,
      pct: 95,
    },
    {
      rank: 2,
      shot: "Elbow pull-up @ HR155+",
      value: "Right 54%",
      severity: "warning" as const,
      pct: 60,
    },
    { rank: 3, shot: "Corner 3PT", value: "Left 38%", severity: "mild" as const, pct: 35 },
  ],
  arc: [
    {
      rank: 1,
      shot: "Mid jumper @ HR160+",
      value: "Flat 83%",
      severity: "critical" as const,
      pct: 100,
    },
    {
      rank: 2,
      shot: "FT line @ HR155+",
      value: "Flat 61%",
      severity: "warning" as const,
      pct: 70,
    },
    { rank: 3, shot: "Elbow pull-up", value: "Flat 42%", severity: "mild" as const, pct: 45 },
  ],
  depth: [
    {
      rank: 1,
      shot: "Deep 3PT @ HR155+",
      value: "Short 78%",
      severity: "critical" as const,
      pct: 90,
    },
    {
      rank: 2,
      shot: "Wing 3PT @ HR170+",
      value: "Long 52%",
      severity: "warning" as const,
      pct: 65,
    },
    { rank: 3, shot: "Mid pull-up", value: "Short 35%", severity: "mild" as const, pct: 40 },
  ],
} as const;

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ── Waveform bars (static seed to avoid random on each render) ── */
const WAVE_BARS = Array.from({ length: 40 }, (_, i) => ({
  id: `wave-${i}`,
  h: Math.max(2, 4 + Math.sin(i * 0.7) * 8 + ((i * 7 + 3) % 5)),
}));

/* ── Static timestamps for session lines ── */
const SESSION_TIMES = ["5:12", "8:34", "12:07", "15:42"];

export function VoiceTranscript() {
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const { t } = useI18n();
  const statusRef = useRef<HTMLSpanElement>(null);
  const statusDotRef = useRef<HTMLSpanElement>(null);

  const ref = useGsapScrollTrigger(
    (el) => {
      if (reducedMotion) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          toggleActions: "play none none none",
        },
      });

      /* ── Chat window fade in ── */
      const chatWindow = el.querySelector(`.${styles.chatWindow}`);
      if (chatWindow) {
        gsap.set(chatWindow, { y: 30, opacity: 0 });
        tl.to(chatWindow, { y: 0, opacity: 1, duration: 0.4, ease: "power2.out" }, 0);
      }

      /* ── Phase A: Session recording bubbles ── */
      const sessionBubbles = el.querySelectorAll(`.${styles.sessionBubble}`);
      let bIdx = 0;
      for (const bubble of sessionBubbles) {
        gsap.set(bubble, { x: 20, opacity: 0 });
        tl.to(bubble, { x: 0, opacity: 1, duration: 0.35, ease: "power3.out" }, 0.3 + bIdx * 0.12);
        const tags = bubble.querySelectorAll(`.${styles.tag}`);
        let tIdx = 0;
        for (const tag of tags) {
          gsap.set(tag, { scale: 0, opacity: 0 });
          tl.to(
            tag,
            { scale: 1, opacity: 1, duration: 0.15, ease: "back.out(2)" },
            0.4 + bIdx * 0.12 + tIdx * 0.04,
          );
          tIdx++;
        }
        bIdx++;
      }

      /* ── Waveform pulse during Phase A ── */
      const waveform = el.querySelector(`.${styles.waveform}`);
      if (waveform) {
        gsap.set(waveform, { opacity: 0 });
        tl.to(waveform, { opacity: 0.6, duration: 0.3 }, 0.2);
        tl.to(waveform, { opacity: 0, duration: 0.4 }, 1.0);
      }

      /* ── Status transitions ── */
      const statusEl = statusRef.current;
      const dotEl = statusDotRef.current;
      if (statusEl && dotEl) {
        tl.to(
          dotEl,
          {
            background: "#ff9500",
            duration: 0.05,
            onStart: () => {
              statusEl.textContent = t.ai.voiceStatusAnalyzing;
            },
          },
          1.0,
        );
        tl.to(
          dotEl,
          {
            background: "#34c759",
            duration: 0.05,
            onStart: () => {
              statusEl.textContent = t.ai.voiceStatusReport;
            },
          },
          1.4,
        );
      }

      /* ── Divider ── */
      const divider = el.querySelector(`.${styles.divider}`);
      if (divider) {
        gsap.set(divider, { scaleX: 0, opacity: 0 });
        tl.to(divider, { scaleX: 1, opacity: 1, duration: 0.3, ease: "power2.out" }, 1.1);
      }

      /* ── Phase B: AI report ── */
      const reportIntro = el.querySelector(`.${styles.reportIntro}`);
      if (reportIntro) {
        gsap.set(reportIntro, { x: -20, opacity: 0 });
        tl.to(reportIntro, { x: 0, opacity: 1, duration: 0.4, ease: "power3.out" }, 1.3);
      }

      const insightCards = el.querySelectorAll(`.${styles.insightCard}`);
      let cIdx = 0;
      for (const card of insightCards) {
        gsap.set(card, { y: 20, opacity: 0 });
        tl.to(card, { y: 0, opacity: 1, duration: 0.4, ease: "power3.out" }, 1.5 + cIdx * 0.15);

        const bars = card.querySelectorAll(`.${styles.barFill}`);
        for (const bar of bars) {
          const target = (bar as HTMLElement).dataset.pct || "0";
          gsap.set(bar, { width: "0%" });
          tl.to(bar, { width: `${target}%`, duration: 0.5, ease: "power2.out" }, 1.6 + cIdx * 0.15);
        }

        const sparkLine = card.querySelector(`.${styles.sparkLine}`);
        if (sparkLine) {
          const len = (sparkLine as SVGPathElement).getTotalLength?.() || 200;
          gsap.set(sparkLine, { strokeDasharray: len, strokeDashoffset: len });
          tl.to(sparkLine, { strokeDashoffset: 0, duration: 0.6, ease: "none" }, 1.6 + cIdx * 0.15);
        }

        /* Arc trajectory path draw */
        const arcPath = card.querySelector(`.${styles.arcPath}`) as SVGPathElement | null;
        if (arcPath) {
          const arcLen = arcPath.getTotalLength?.() || 200;
          gsap.set(arcPath, { strokeDasharray: arcLen, strokeDashoffset: arcLen });
          tl.to(
            arcPath,
            { strokeDashoffset: 0, duration: 0.6, ease: "power2.out" },
            1.6 + cIdx * 0.15,
          );
        }

        /* Drill steps stagger */
        const drillSteps = card.querySelectorAll(`.${styles.drillStep}`);
        if (drillSteps.length > 0) {
          gsap.set(drillSteps, { x: 15, opacity: 0 });
          let dIdx = 0;
          for (const step of drillSteps) {
            tl.to(
              step,
              { x: 0, opacity: 1, duration: 0.25, ease: "power3.out" },
              1.6 + cIdx * 0.15 + dIdx * 0.08,
            );
            dIdx++;
          }
        }

        /* Error ranking items stagger */
        const errorItems = card.querySelectorAll(`.${styles.errorItem}`);
        if (errorItems.length > 0) {
          gsap.set(errorItems, { x: 10, opacity: 0 });
          let eIdx = 0;
          for (const item of errorItems) {
            tl.to(
              item,
              { x: 0, opacity: 1, duration: 0.2, ease: "power3.out" },
              1.6 + cIdx * 0.15 + eIdx * 0.05,
            );
            eIdx++;
          }
        }

        /* Worst badge scale pop */
        const worstBadges = card.querySelectorAll(`.${styles.worstBadge}`);
        for (const wb of worstBadges) {
          gsap.set(wb, { scale: 0 });
          tl.to(wb, { scale: 1, duration: 0.2, ease: "back.out(2)" }, 1.9 + cIdx * 0.15);
        }

        /* Critical insight fade in */
        const criticalInsight = card.querySelector(`.${styles.criticalInsight}`);
        if (criticalInsight) {
          gsap.set(criticalInsight, { y: 15, opacity: 0 });
          tl.to(
            criticalInsight,
            { y: 0, opacity: 1, duration: 0.3, ease: "power2.out" },
            2.0 + cIdx * 0.15,
          );
        }

        const badge = card.querySelector(`.${styles.changeBadge}`);
        if (badge) {
          gsap.set(badge, { scale: 0 });
          tl.to(badge, { scale: 1, duration: 0.25, ease: "back.out(2)" }, 1.8 + cIdx * 0.15);
        }
        cIdx++;
      }
    },
    [isMobile],
  );

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.content}>
        <h2 className={styles.title}>{t.ai.voiceTitle}</h2>

        <div className={styles.chatWindow}>
          {/* Chat header with status */}
          <div className={styles.chatHeader}>
            <span ref={statusDotRef} className={styles.statusDot} />
            <span ref={statusRef} className={styles.statusText}>
              {t.ai.voiceStatusRecording}
            </span>
          </div>

          <div className={styles.chatMessages}>
            {/* ── Waveform overlay ── */}
            <div className={styles.waveform}>
              <svg
                viewBox="0 0 200 24"
                preserveAspectRatio="none"
                className={styles.waveformSvg}
                role="img"
                aria-label="Audio waveform"
              >
                <title>Audio waveform</title>
                {WAVE_BARS.map((bar) => (
                  <rect
                    key={bar.id}
                    x={Number.parseInt(bar.id.split("-")[1], 10) * 5}
                    y={12 - bar.h / 2}
                    width={3}
                    height={bar.h}
                    rx={1.5}
                    fill="var(--color-ai-primary)"
                    opacity={0.4}
                  />
                ))}
              </svg>
            </div>

            {/* ── Phase A: Session voice lines ── */}
            {t.ai.voiceSessionLines.map((line, lineIdx) => (
              <div
                key={`session-${line.text.slice(0, 16)}`}
                className={`${styles.sessionBubble} ${styles.bubbleUser}`}
              >
                <div className={styles.bubbleTop}>
                  <span className={styles.bubbleLabel}>{line.speaker}</span>
                  <span className={styles.bubbleTime}>{SESSION_TIMES[lineIdx] ?? "0:00"}</span>
                </div>
                <p className={styles.bubbleText}>{line.text}</p>
                {line.tags && line.tags.length > 0 && (
                  <div className={styles.tagRow}>
                    {line.tags.map((tag) => (
                      <span
                        key={`${line.text.slice(0, 8)}-${tag.label}`}
                        className={`${styles.tag} ${styles[`tag${tag.type.charAt(0).toUpperCase()}${tag.type.slice(1)}`]}`}
                      >
                        {tag.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* ── Divider ── */}
            <div className={styles.divider}>
              <span className={styles.dividerLine} />
              <span className={styles.dividerText}>{t.ai.voiceStatusAnalyzing}</span>
              <span className={styles.dividerLine} />
            </div>

            {/* ── Phase B: AI Report intro ── */}
            <div className={`${styles.reportIntro} ${styles.bubbleAi}`}>
              <span className={styles.bubbleLabel}>AI</span>
              <p className={styles.bubbleText}>{t.ai.voiceReportIntro}</p>
            </div>
          </div>
        </div>

        {/* ── Insight Cards (outside chat window, full width) ── */}
        <div className={styles.insightGrid}>
          {t.ai.voiceInsightCards.map((card) => (
            <div key={card.title} className={styles.insightCard}>
              <div className={styles.insightHeader}>
                <span className={styles.insightIcon}>{card.icon}</span>
                <span className={styles.insightTitle}>{card.title}</span>
                <span
                  className={`${styles.changeBadge} ${card.changeType === "positive" ? styles.changePositive : styles.changeNegative}`}
                >
                  {card.change}
                </span>
              </div>

              {/* Bar chart for weakness analysis card (zoneFg) */}
              {card.chartType === "zoneFg" && (
                <div className={styles.barChart}>
                  {ZONE_BARS.map((bar) => (
                    <div key={bar.label} className={styles.barRow}>
                      <span className={styles.barLabel}>{bar.label}</span>
                      <div className={styles.barTrack}>
                        <div
                          className={styles.barFill}
                          data-pct={bar.pct}
                          style={{ background: bar.color }}
                        />
                      </div>
                      <span className={styles.barPct}>{bar.pct}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* 3D Error Ranking for arcPattern card */}
              {card.chartType === "arcPattern" && (
                <div className={styles.errorRanking}>
                  {/* Direction Section */}
                  <div className={styles.errorDimension}>
                    <div className={styles.errorDimLabel}>
                      <span className={styles.errorDimIcon}>{"\u2194"}</span>
                      <span>{t.ai.errorDirection.title}</span>
                    </div>
                    {ERROR_RANKINGS.direction.map((item) => (
                      <div key={item.rank} className={styles.errorItem}>
                        <span
                          className={`${styles.errorRank} ${styles[`severity${capitalize(item.severity)}`]}`}
                        >
                          #{item.rank}
                        </span>
                        <span className={styles.errorShot}>{item.shot}</span>
                        <span className={styles.errorValue}>{item.value}</span>
                        {item.rank === 1 && <span className={styles.worstBadge}>WORST</span>}
                      </div>
                    ))}
                  </div>

                  {/* Arc Section */}
                  <div className={styles.errorDimension}>
                    <div className={styles.errorDimLabel}>
                      <span className={styles.errorDimIcon}>{"\u2312"}</span>
                      <span>{t.ai.errorArc.title}</span>
                    </div>
                    {ERROR_RANKINGS.arc.map((item) => (
                      <div key={item.rank} className={styles.errorItem}>
                        <span
                          className={`${styles.errorRank} ${styles[`severity${capitalize(item.severity)}`]}`}
                        >
                          #{item.rank}
                        </span>
                        <span className={styles.errorShot}>{item.shot}</span>
                        <span className={styles.errorValue}>{item.value}</span>
                        {item.rank === 1 && <span className={styles.worstBadge}>WORST</span>}
                      </div>
                    ))}
                  </div>

                  {/* Depth Section */}
                  <div className={styles.errorDimension}>
                    <div className={styles.errorDimLabel}>
                      <span className={styles.errorDimIcon}>{"\u2195"}</span>
                      <span>{t.ai.errorDepth.title}</span>
                    </div>
                    {ERROR_RANKINGS.depth.map((item) => (
                      <div key={item.rank} className={styles.errorItem}>
                        <span
                          className={`${styles.errorRank} ${styles[`severity${capitalize(item.severity)}`]}`}
                        >
                          #{item.rank}
                        </span>
                        <span className={styles.errorShot}>{item.shot}</span>
                        <span className={styles.errorValue}>{item.value}</span>
                        {item.rank === 1 && <span className={styles.worstBadge}>WORST</span>}
                      </div>
                    ))}
                  </div>

                  {/* Arc SVG visual: worst arc comparison */}
                  <div className={styles.arcVisual}>
                    <svg
                      viewBox="0 0 300 100"
                      className={styles.arcSvg}
                      role="img"
                      aria-label="Arc trajectory comparison"
                    >
                      <title>Arc trajectory comparison</title>
                      {/* Backboard + rim */}
                      <rect
                        x="248"
                        y="50"
                        width="4"
                        height="30"
                        rx="1"
                        fill="rgba(255,255,255,0.15)"
                      />
                      <line
                        x1="232"
                        y1="68"
                        x2="252"
                        y2="68"
                        stroke="rgba(255,255,255,0.3)"
                        strokeWidth="2"
                      />
                      {/* Ideal arc (green, dashed) */}
                      <path
                        d="M 40 85 Q 145 -10 240 66"
                        fill="none"
                        stroke="#34c759"
                        strokeWidth="2"
                        strokeDasharray="6 3"
                        opacity="0.6"
                      />
                      {/* Flat arc — miss (red) */}
                      <path
                        d={ARC_FLAT}
                        className={styles.arcPath}
                        fill="none"
                        stroke="#ff3b30"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      {/* Labels */}
                      <text
                        x="100"
                        y="20"
                        fill="#34c759"
                        fontSize="9"
                        fontWeight="600"
                        opacity="0.8"
                      >
                        IDEAL ARC
                      </text>
                      <text
                        x="110"
                        y="58"
                        fill="#ff3b30"
                        fontSize="9"
                        fontWeight="600"
                        opacity="0.8"
                      >
                        FLAT 83%
                      </text>
                      {/* Release point */}
                      <circle
                        cx="40"
                        cy="85"
                        r="4"
                        fill="rgba(255,255,255,0.2)"
                        stroke="rgba(255,255,255,0.4)"
                        strokeWidth="1"
                      />
                      {/* Label for worst shot */}
                      <text
                        x="40"
                        y="97"
                        fill="rgba(255,255,255,0.5)"
                        fontSize="7"
                        fontWeight="500"
                        textAnchor="middle"
                      >
                        Mid jumper @ HR160+
                      </text>
                    </svg>
                  </div>

                  {/* Critical insight callout */}
                  <div className={styles.criticalInsight}>
                    <span className={styles.criticalIcon}>{"\u26A0\uFE0F"}</span>
                    <p>{t.ai.errorArc.desc}</p>
                  </div>
                </div>
              )}

              {/* Drill plan timeline for drill plan card (drillPlan) */}
              {card.chartType === "drillPlan" && (
                <div className={styles.drillPlan}>
                  {t.ai.voiceDrillSteps.map((step) => (
                    <div key={step.id} className={styles.drillStep}>
                      <div className={styles.drillTimeline}>
                        <div className={styles.drillDot} style={{ background: step.color }} />
                        <div className={styles.drillLine} />
                      </div>
                      <div className={styles.drillContent}>
                        <span className={styles.drillTime}>{step.time}</span>
                        <span className={styles.drillLabel}>{step.label}</span>
                        <span className={styles.drillDetail}>{step.detail}</span>
                        {step.context && (
                          <span
                            className={styles.drillDetail}
                            style={{ fontStyle: "italic", opacity: 0.8 }}
                          >
                            {step.context}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className={styles.insightValue}>{card.value}</p>
            </div>
          ))}
        </div>

        <HrZoneMatrix />
      </div>
    </section>
  );
}
